import { useState } from 'react';
import { useAuth } from '../auth/useAuth';
import {
  openPortal,
  startCheckout,
  type PlanInterval,
  type RedirectUrl,
} from '../api/billing';
import { ApiError, TimeoutError } from '../api/client';
import { billingSummary } from './billingView';

/// Subscription card on the dashboard. Free accounts see a Monthly/Annual
/// toggle + Subscribe (→ Stripe Checkout); Pro accounts see their plan + Manage
/// billing (→ Stripe Customer Portal). Both are redirects to Stripe-hosted
/// pages — the server returns the URL and we navigate to it.
export default function BillingCard() {
  const { user } = useAuth();
  const [interval, setInterval] = useState<PlanInterval>('monthly');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;
  const summary = billingSummary(user);

  async function redirectVia(action: () => Promise<RedirectUrl>) {
    setBusy(true);
    setError('');
    try {
      const { url } = await action();
      window.location.assign(url);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else if (err instanceof TimeoutError) setError('Request timed out. Please try again.');
      else setError('Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>Subscription</h2>

      {error && <div className="error-message">{error}</div>}

      {summary.isPro ? (
        <div className="profile-info">
          <dl>
            <dt>Plan</dt>
            <dd>{summary.planLabel}</dd>
            {summary.through && (
              <>
                <dt>Through</dt>
                <dd>{summary.through}</dd>
              </>
            )}
          </dl>
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => redirectVia(openPortal)}
          >
            {busy ? 'Opening…' : 'Manage billing'}
          </button>
        </div>
      ) : (
        <div className="profile-info">
          <p>
            You're on the <strong>Free</strong> plan.
          </p>
          <div className="form-group">
            <label>
              <input
                type="radio"
                name="billing-term"
                checked={interval === 'monthly'}
                onChange={() => setInterval('monthly')}
                disabled={busy}
              />{' '}
              Monthly
            </label>
            <label>
              <input
                type="radio"
                name="billing-term"
                checked={interval === 'annual'}
                onChange={() => setInterval('annual')}
                disabled={busy}
              />{' '}
              Annual
            </label>
          </div>
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => redirectVia(() => startCheckout(interval))}
          >
            {busy ? 'Redirecting…' : 'Subscribe'}
          </button>
        </div>
      )}
    </section>
  );
}
